// src/stage2/sceneExterior/PlaygroundScene.jsx
import React, { useRef, useLayoutEffect, useState } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { buildPlaygroundColliders } from '../collision/CollisionStore';
import { BikesField } from './BikesField';
import { TablesField } from './TablesField';
import { TreesField } from './TreesField';
import { BrickWallField } from './perimeter/BrickWallScene';
import { RingsField } from './ringsOfEncouragement/RingsField';
import { BushesField } from './BushesField';
import { TreelineForeground } from './perimeter/TreelineForeground';
import { ChainGateField } from './perimeter/ChainGateField';
import { MountainBackdrop } from './perimeter/MountainBackdrop';
import { ParkingBackdrop } from './perimeter/ParkingBackdrop';
import { AnimatedGate } from './perimeter/AnimatedGate'
import { GateDoor } from './perimeter/GateDoor';
import { TreelineRoad } from './perimeter/TreelineRoad';
import { NeighborhoodBackdrop } from './perimeter/neighborhoodBackdrop';

// Core environment (ground + school)
const GROUND_URL     = '/stageAssets/stage2/scene/Stage2Exterior-ENVandSCHOOL.glb';
// Single big playground chunk for now
const PLAY_EQUIP_URL = '/stageAssets/stage2/scene/Stage2Exterior-Playground.glb';
const SIGN = '/stageAssets/stage2/scene/CourtyardSign.glb'

export function PlaygroundScene({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  castShadows = true,
  receiveShadows = true,
  onRideStart,
  onRingCollect,
  onBikeNearChange,
  bikesLiveRef
}) {
  const groupRef = useRef();

  // GLTFs MUST be loaded unconditionally (no try/catch, no if)
  const ground     = useGLTF(GROUND_URL);
  const playground = useGLTF(PLAY_EQUIP_URL);
  // const trees = useGLTF(TREES_URL); // <- only once you really have this file
  const gardenSign = useGLTF(SIGN);
  //Tracking AnimatedGate (Chain Gate Door) Open
  const [isOpen, setOpen] = useState(false);


  useLayoutEffect(() => {
    if (!groupRef.current) return;

    groupRef.current.traverse((obj) => {
      if (!obj.isMesh) return;

      obj.castShadow = !!castShadows;
      obj.receiveShadow = !!receiveShadows;

      if (obj.material) {
        obj.material.side = THREE.FrontSide;
        obj.material.needsUpdate = true;
      }
    });

    buildPlaygroundColliders(groupRef.current);
  }, [castShadows, receiveShadows]);

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={scale}>
      {/* Core pieces */}
      <primitive object={ground.scene} />
      {/* <primitive object={playground.scene} /> */}
      {/* {trees && <primitive object={trees.scene} />} */}
      <primitive object={gardenSign.scene} scale={0.35} position={[-5.5, 1.78, 14.2]}/>
      {/* Instanced objects */}
      <BikesField
        onRideStart={onRideStart}
        onBikeNearChange={onBikeNearChange}
        bikesLiveRef={bikesLiveRef}
      />      
      <TablesField />
      <TreesField />
      <RingsField onRingCollect={onRingCollect}/>

      {/* 3D ASSET PERIMETER */}
      <BrickWallField />
      <ChainGateField />
      <AnimatedGate
        position={[38.4, 3, -3.85]}
        rotation={[0, 1.5, 0]}
        scale={1.15}
        open = {isOpen} // pass open state as a prop "on click" or "when close" to trigger animation
      />
      <GateDoor
        position={[0, 0.2, -14]}
        rotation={[0, 3.2, 0]}
      />      
      {/* <AnimatedGate
        position={[-1, 0, -16]}
        rotation={[0, 3, 0]}
        scale={2}
        open = {isOpen} // pass open state as a prop "on click" or "when close" to trigger animation
      /> */}
      {/* <BushesField /> */}
      {/* PNG IMAGE BORDER */}
      <NeighborhoodBackdrop />
      <ParkingBackdrop/>
      <TreelineRoad />
      <TreelineForeground />
       {/* treeline behind it */}

      <MountainBackdrop />
    </group>
  );
}

// Preload the ones you *know* exist
useGLTF.preload(GROUND_URL);
useGLTF.preload(PLAY_EQUIP_URL);
// useGLTF.preload(TREES_URL);
